import type { MouseEventHandler } from 'react';
import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box } from '@atlaskit/primitives/compiled';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';

import { preloadChatOnIntent } from '@atlassian/conversation-assistant/preload-chat-on-intent';
import { ConversationAssistantButtonNav4 } from '@atlassian/conversation-assistant-ui-components';
import { ChatContextSubscriber } from '@atlassian/conversation-assistant-store';
import {
	CONVERSATION_ASSISTANT_BRAND_NAME,
	ChatOpenerSubscriber,
	ConversationAssistantUIModes,
} from '@atlassian/conversation-assistant';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	isMac,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	AI_MATE_SHORTCUT,
	UNIVERSAL_AI_MATE_SHORTCUT_MAC,
	UNIVERSAL_AI_MATE_SHORTCUT_WIN,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import { useToggleConversationAssistant } from './hooks/useToggleConversationAssistant.ts';

export const ConversationAssistantNavBarButton = (): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const { mode, toggleConversationAssistant, isConversationAssistantSidebarActive } =
		useToggleConversationAssistant();

	const handleOnClick = useCallback<MouseEventHandler>(
		(e) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'navigationItem',
					actionSubjectId: 'aiMate',
					source: 'AIMate',
					attributes: {
						opened: !isConversationAssistantSidebarActive,
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: '4',
					},
					tags: ['atlaskit', 'aiMate'],
				},
			}).fire();
			e.preventDefault();
			toggleConversationAssistant();
		},
		[createAnalyticsEvent, isConversationAssistantSidebarActive, toggleConversationAssistant],
	);

	const handleMouseEnter = useCallback<MouseEventHandler>(() => {
		// Intent-time preload — FG-gated, once-only per page load, so safe on every hover.
		if (!isConversationAssistantSidebarActive) {
			preloadChatOnIntent();
		}
	}, [isConversationAssistantSidebarActive]);

	const universalAIMateShortcut = isMac()
		? UNIVERSAL_AI_MATE_SHORTCUT_MAC
		: UNIVERSAL_AI_MATE_SHORTCUT_WIN;

	const shortcutStrings = useShortcutStrings({ shortcut: universalAIMateShortcut });

	return (
		<Box as="span" paddingInlineStart="space.050" testId="app-navigation-ai-mate">
			<ConversationAssistantButtonNav4
				siteId={cloudId}
				productKey="confluence"
				onClick={handleOnClick}
				onMouseEnter={handleMouseEnter}
				isActive={isConversationAssistantSidebarActive}
				tooltipContent={CONVERSATION_ASSISTANT_BRAND_NAME}
				shortcut={
					// No shortcut on edit pages
					!isLiveEditMode && !isOnEditRoute ? shortcutStrings : undefined
				}
			/>
			<ChatContextSubscriber />
			<ChatOpenerSubscriber
				onTriggerOpeningChat={(conversationId, openChatMode) => {
					if (
						isConversationAssistantSidebarActive ||
						mode === ConversationAssistantUIModes.MINI_MODAL
					) {
						return;
					}

					conversationId &&
						updatePageRovoParams({
							pathway: 'chat',
							conversationId,
						});

					toggleConversationAssistant(openChatMode);
				}}
			/>
			<GeneralShortcutListener
				deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? AI_MATE_SHORTCUT : undefined}
				accelerator={universalAIMateShortcut}
				listener={() => toggleConversationAssistant('sidebar')}
				isAppNavigationShortcut
			/>
		</Box>
	);
};
